import React, { useState } from 'react';
import {
  Cloud, CloudFog, CloudLightning, CloudRain, CloudSnow,
  Sun, Sunrise, Sunset, Wind, Droplets
} from 'lucide-react';
import { useWeatherSun, DayForecast } from '../hooks/useWeatherSun';
import { useTranslation } from 'react-i18next';
import WeatherForecastModal from './WeatherForecastModal';

// ─── Weather icon ─────────────────────────────────────────────────────────────

const WeatherIcon = ({ symbol, size = 16 }: { symbol: string; size?: number }) => {
  if (symbol.includes('clearsky') || symbol.includes('fair'))
    return <Sun size={size} className="text-amber-400" />;
  if (symbol.includes('partlycloudy'))
    return (
      <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
        <Sun size={Math.max(8, size - 4)} className="text-amber-400 absolute -top-1 -right-1" />
        <Cloud size={size} className="text-[var(--text-secondary)]" />
      </div>
    );
  if (symbol.includes('cloudy')) return <Cloud size={size} className="text-[var(--text-secondary)]" />;
  if (symbol.includes('thunder')) return <CloudLightning size={size} className="text-amber-300" />;
  if (symbol.includes('snow') || symbol.includes('sleet'))
    return <CloudSnow size={size} className="text-blue-200" />;
  if (symbol.includes('rain') || symbol.includes('shower'))
    return <CloudRain size={size} className="text-blue-400" />;
  if (symbol.includes('fog')) return <CloudFog size={size} className="text-[var(--text-muted)]" />;
  return <Cloud size={size} className="text-[var(--text-secondary)]" />;
};

// ─── Wind label + colour ──────────────────────────────────────────────────────

const windLabel = (ms: number, t: any) => {
  if (ms < 3) return { label: t('weather.wind_still'), cls: 'text-emerald-400' };
  if (ms < 8) return { label: `${ms} m/s`, cls: 'text-emerald-400' };
  if (ms < 14) return { label: `${ms} m/s`, cls: 'text-amber-400' };
  return { label: `${ms} m/s`, cls: 'text-red-400' };
};

// ─── Main component ───────────────────────────────────────────────────────────

interface WeatherSunBadgeProps {
  lat: number;
  lon: number;
}

const WeatherSunBadge: React.FC<WeatherSunBadgeProps> = ({ lat, lon }) => {
  const { t } = useTranslation();
  const { weather, sun, snow, error, loading } = useWeatherSun(lat, lon);
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (loading)
    return (
      <div className="h-11 flex items-center justify-center">
        <div className="w-4 h-4 border-2 border-blue-500/20 border-t-blue-500 rounded-full animate-spin" />
      </div>
    );
  if (error) return <div className="text-3xs text-[var(--text-muted)] italic">{t(error)}</div>;
  if (!weather || !sun) return null;

  const wind = windLabel(weather.today.windMs, t);

  return (
    <>
      <div 
        className="bg-[var(--bg-surface-alt)] backdrop-blur-sm border border-[var(--border-secondary)] rounded-2xl overflow-hidden cursor-pointer hover:bg-[var(--bg-surface)] transition-colors"
        onClick={() => setIsModalOpen(true)}
        title={t('weather.show_forecast_details', 'Vis detaljert værvarsel')}
      >
        <div className="flex items-center gap-1.5 px-2 py-1.5">

          {/* Today + Tomorrow */}
          <div className="flex gap-2 border-r border-[var(--border-secondary)] pr-2">
            <div className="flex flex-col items-center gap-0.5">
              <span className="text-[7px] font-black uppercase tracking-wide text-[var(--text-muted)]">{t('weather.today')}</span>
              <div className="flex items-center gap-1">
                <WeatherIcon symbol={weather.today.symbol} size={14} />
                <span className="text-2xs font-bold text-[var(--text-primary)] leading-none">
                  {weather.today.maxTemp}°
                  <span className="hidden sm:inline text-4xs text-[var(--text-muted)] font-medium">/{weather.today.minTemp}°</span>
                </span>
              </div>
            </div>
            <div className="flex flex-col items-center gap-0.5">
              <span className="text-[7px] font-black uppercase tracking-wide text-[var(--text-muted)]">{t('weather.tomorrow')}</span>
              <div className="flex items-center gap-1">
                <WeatherIcon symbol={weather.tomorrow.symbol} size={14} />
                <span className="text-2xs font-bold text-[var(--text-primary)] leading-none">
                  {weather.tomorrow.maxTemp}°
                  <span className="hidden sm:inline text-4xs text-[var(--text-muted)] font-medium">/{weather.tomorrow.minTemp}°</span>
                </span>
              </div>
            </div>
          </div>

          {/* Wind */}
          <div className="flex flex-col items-center gap-0.5 border-r border-[var(--border-secondary)] pr-2">
            <span className="text-[7px] font-black uppercase tracking-wide text-[var(--text-muted)]">{t('weather.wind')}</span>
            <div className={`flex items-center gap-0.5 ${wind.cls}`}>
              <Wind size={11} />
              <span className="text-3xs font-bold leading-none">
                {weather.today.windMs < 3 ? '—' : `${weather.today.windMs}`}
                {weather.today.windMs >= 3 && <span className="text-[7px] ml-0.5">m/s</span>}
              </span>
            </div>
          </div>

          {/* Rain (only if > 0) */}
          {weather.today.rainMm > 0 && (
            <div className="flex flex-col items-center gap-0.5 border-r border-[var(--border-secondary)] pr-2">
              <span className="text-[7px] font-black uppercase tracking-wide text-[var(--text-muted)]">{t('weather.precipitation')}</span>
              <div className="flex items-center gap-0.5 text-blue-400">
                <Droplets size={11} />
                <span className="text-3xs font-bold leading-none">
                  {weather.today.rainMm}<span className="text-[7px] ml-0.5">mm</span>
                </span>
              </div>
            </div>
          )}

          {/* Sun */}
          <div className="flex flex-col gap-1 flex-1 min-w-[92px]">
            <div className="flex items-center justify-between gap-1.5 px-0.5">
              <div className="flex flex-col items-center gap-0.5">
                <Sunrise size={11} className="text-amber-500/70" />
                <span className="text-4xs font-bold text-[var(--text-secondary)]">{sun.sunrise}</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-[7px] font-black text-amber-500/90 uppercase tracking-tight leading-none mb-0.5">{t('weather.daylight')}</span>
                <span className="text-3xs font-black text-amber-500/90 uppercase tracking-tight">
                  {sun.daylightHours}{t('weather.hours_suffix')}
                </span>
              </div>
              <div className="flex flex-col items-center gap-0.5">
                <Sunset size={11} className="text-orange-500/70" />
                <span className="text-4xs font-bold text-[var(--text-secondary)]">{sun.sunset}</span>
              </div>
            </div>
            {/* Sun arc */}
            <div className="relative h-1 w-full bg-[var(--bg-input)] rounded-full overflow-hidden">
              <div
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-amber-500 via-yellow-400 to-orange-500 transition-all duration-1000"
                style={{ width: `${sun.sunPosition * 100}%` }}
              />
            </div>
          </div>

          {/* Snow (if present) */}
          {snow && snow.depthCm > 0 && (
            <div className="flex flex-col items-center justify-center gap-0.5 border-l border-[var(--border-secondary)] pl-2 min-w-[44px]">
              <span className="hidden sm:block text-[7px] font-black uppercase tracking-wide text-sky-200">{t('weather.snow')}</span>
              <div className="flex items-center gap-0.5 text-sky-100">
                <CloudSnow size={11} />
                <span className="text-2xs font-black leading-none">
                  {Math.round(snow.depthCm)}<span className="text-4xs ml-0.5">{t('weather.snow_unit')}</span>
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <WeatherForecastModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        lat={lat}
        lon={lon}
      />
    </>
  );
};

export default WeatherSunBadge;
